import React from 'react';
import { Line, Doughnut } from 'react-chartjs-2';
import { SERIES, orderTypeColor, moneyTick } from '../lib/charts';
import useReport from '../lib/useReport';
import { useFilters } from '../contexts/FilterContext';
import { money, moneyCompact, int, niceDate } from '../lib/format';
import { Card, KpiCard, Spinner, ErrorBox, Empty, DataTable, Delta } from '../components/ui';

export default function Overview() {
  const { reportParams, startDate, endDate, compareMode, compareLabel } = useFilters();

  const summary = useReport('summary', { ...reportParams, compare: compareMode });
  const trend = useReport('sales-trend', { ...reportParams, granularity: 'day' });
  const topItems = useReport('top-items', { ...reportParams, top: 8 });
  const locations = useReport('locations-performance', { ...reportParams, compare: compareMode });

  if (summary.error) return <ErrorBox message={summary.error} />;
  if (summary.loading || !summary.data) return <Spinner label="Crunching the numbers…" />;

  const { current: c, previous: p, byOrderType } = summary.data;

  const trendData = {
    labels: (trend.data || []).map(r => niceDate(r.date)),
    datasets: [{
      label: 'Net sales',
      data: (trend.data || []).map(r => r.netSales),
      borderColor: SERIES[0],
      backgroundColor: 'rgba(42, 120, 214, 0.08)',
      fill: true,
      borderWidth: 2,
      pointRadius: (trend.data || []).length > 45 ? 0 : 2.5,
      pointHoverRadius: 5,
      tension: 0.25,
    }],
  };

  const donutData = {
    labels: byOrderType.map(r => r.orderType),
    datasets: [{
      data: byOrderType.map(r => r.netSales),
      backgroundColor: byOrderType.map(r => orderTypeColor(r.orderType)),
      borderColor: '#fcfcfb',
      borderWidth: 2,
    }],
  };

  return (
    <div className="grid" style={{ gap: 14 }}>
      <div className="grid kpi-grid">
        <KpiCard label="Net Sales" value={moneyCompact(c.netSales)} curr={c.netSales} prev={p.netSales} sub={compareLabel} />
        <KpiCard label="Orders" value={int(c.orders)} curr={c.orders} prev={p.orders} sub={compareLabel} />
        <KpiCard label="Avg Order Value" value={money(c.avgOrderValue)} curr={c.avgOrderValue} prev={p.avgOrderValue} sub="net, per order" />
        <KpiCard label="Tips" value={moneyCompact(c.tips)} curr={c.tips} prev={p.tips} sub="all payment types" />
        <KpiCard
          label="Online Share"
          value={c.orders ? `${((c.onlineOrders / c.orders) * 100).toFixed(1)}%` : '—'}
          curr={c.onlineOrders} prev={p.onlineOrders}
          sub={`${int(c.onlineOrders)} online orders`}
        />
        <KpiCard label="Discounts" value={moneyCompact(c.discounts)} sub="applied to orders" invert />
        <KpiCard label="Gift Cards Sold" value={moneyCompact(c.giftCardsSold || 0)} sub={`${int(c.giftCardCount || 0)} sold · not in Net Sales`} />
        <KpiCard
          label="Failed / Cancelled"
          value={int(c.failedOrders)}
          sub={`${money(c.failedAmount)} value`}
          invert
        />
      </div>

      <div className="grid main-side">
        <Card
          tour="ov-trend"
          title="Net Sales Trend"
          sub={`${niceDate(startDate)} – ${niceDate(endDate)} · daily, business day starts 4 AM`}
        >
          <div className="chart-box">
            {trend.loading ? <Spinner /> : trend.error ? <ErrorBox message={trend.error} /> : (
              <Line
                data={trendData}
                options={{
                  responsive: true,
                  maintainAspectRatio: false,
                  plugins: {
                    legend: { display: false },
                    tooltip: {
                      callbacks: {
                        label: ctx => ` ${money(ctx.parsed.y)} · ${int(trend.data[ctx.dataIndex]?.orders)} orders`,
                      },
                    },
                  },
                  scales: {
                    y: { ticks: { callback: moneyTick }, beginAtZero: true },
                    x: { grid: { display: false }, ticks: { maxTicksLimit: 10 } },
                  },
                }}
              />
            )}
          </div>
        </Card>

        <Card tour="ov-mix" title="Sales Mix by Order Type" sub="net sales share">
          <div className="chart-box">
            {byOrderType.length === 0 ? <Empty /> : (
              <Doughnut
                data={donutData}
                options={{
                  responsive: true,
                  maintainAspectRatio: false,
                  cutout: '62%',
                  plugins: {
                    legend: { position: 'bottom' },
                    tooltip: {
                      callbacks: {
                        label: ctx => {
                          const row = byOrderType[ctx.dataIndex];
                          const share = c.netSales ? ((row.netSales / c.netSales) * 100).toFixed(1) : 0;
                          return ` ${money(row.netSales)} (${share}%) · ${int(row.orders)} orders`;
                        },
                      },
                    },
                  },
                }}
              />
            )}
          </div>
        </Card>
      </div>

      <div className="grid two-col">
        <Card tour="ov-locations" title="Top Locations" sub={`net sales this period ${compareLabel}`}>
          {locations.loading ? <Spinner /> : locations.error ? <ErrorBox message={locations.error} /> : (
            <DataTable
              maxRows={8}
              defaultSort={{ key: 'netSales', dir: 'desc' }}
              columns={[
                { key: 'locationName', label: 'Location' },
                { key: 'netSales', label: 'Net Sales', num: true, render: r => money(r.netSales) },
                { key: 'orders', label: 'Orders', num: true, render: r => int(r.orders) },
                {
                  key: 'change', label: compareMode === 'priorYear' ? 'vs LY' : 'vs Prior', num: true, noSort: true,
                  render: r => <Delta curr={r.netSales} prev={r.prevNetSales} />,
                },
              ]}
              rows={(locations.data || []).map(r => ({ ...r, __key: r.locationId }))}
            />
          )}
        </Card>

        <Card tour="ov-items" title="Top Items" sub="by quantity sold">
          {topItems.loading ? <Spinner /> : topItems.error ? <ErrorBox message={topItems.error} /> : (
            <DataTable
              defaultSort={{ key: 'quantity', dir: 'desc' }}
              columns={[
                { key: 'itemName', label: 'Item' },
                { key: 'quantity', label: 'Qty', num: true, render: r => int(r.quantity) },
                { key: 'revenue', label: 'Revenue', num: true, render: r => money(r.revenue) },
              ]}
              rows={topItems.data || []}
            />
          )}
        </Card>
      </div>
    </div>
  );
}
